import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { renderPartnerLogo, partnersContent } from './utils';

const SponsorsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Strategic Sponsors
  const strategicSponsors = [];

  // Diamond Sponsors
  const diamondSponsors = [];

  // Gold Sponsors
  const goldSponsors = [];

  // Silver Sponsors
  const silverSponsors = [];

  // Technical Partner
  const technicalPartners = [
    {
      id: 1,
      name: "للخدمات البرمجية QK Dev",
      logo: "/assets/images/Qk-dev-no-bg -partner-logo.png",
      logoAlt: "QK Dev Software Services logo",
      website: "https://www.qk-dev.com"
    }
  ];

  // Financial Sponsor
  const financialSponsors = [
    {
      id: 1,
      name: "نون بوست",
      logo: "/assets/logos/Noonpost_logos.jpg",
      logoAlt: "نون بوست - راعي",
      website: "https://www.noonpost.com"
    },
    {
      id: 2,
      name: "سكون ميديا",
      logo: "/assets/logos/sukun.png",
      logoAlt: "سكون ميديا - راعي",
      website: "https://www.sokoonmedia.com"
    },
    {
      id: 3,
      name: "حلويات المختار",
      logo: "/assets/logos/حلويات المختار_page-0001.jpg",
      logoAlt: "حلويات المختار - راعي",
      website: ""
    }
  ];

  const content = partnersContent?.[currentLanguage];

  const renderSponsorCategory = (title, partners, index) => {
    const isEmpty = !partners || partners.length === 0;
    const emptyText = currentLanguage === 'ar' ? 'فارغ' : currentLanguage === 'tr' ? 'Boş' : 'Empty';

    return (
      <div key={index} className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="mb-6"
        >
          <h4 className="text-lg md:text-xl font-semibold mb-2">
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {title}
            </span>
          </h4>
          <div className="flex items-center justify-center gap-1">
            <div className="h-0.5 w-8 bg-gradient-to-r from-transparent to-primary/50 rounded-full"></div>
            <div className="h-0.5 w-1 bg-primary/50 rounded-full"></div>
            <div className="h-0.5 w-8 bg-gradient-to-l from-transparent to-primary/50 rounded-full"></div>
          </div>
        </motion.div>
        {isEmpty ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-background border-2 border-border rounded-lg p-6 shadow-sm transition-all duration-300 flex flex-col items-center justify-center min-h-[200px]"
          >
            <span className="text-gray-500 text-sm">{emptyText}</span>
          </motion.div>
        ) : (
          <div className="mt-4 space-y-4">
            {partners.map((partner) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="bg-background border-2 border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center"
              >
                {partner.logo && partner.logo !== "" ? (
                  <>
                    <div className={`flex items-center justify-center w-full ${partner.isLarge ? 'h-32' : 'h-24'}`}>
                      {renderPartnerLogo(partner)}
                    </div>
                    <p className="text-base md:text-2xl font-medium text-gray-800 mt-6 text-center">
                      {partner.name}
                    </p>
                    {partner.description && (
                      <p className="text-sm md:text-base text-gray-600 mt-4 text-center leading-relaxed whitespace-pre-line">
                        {partner.description}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="text-base md:text-2xl text-center">{partner.name}</div>
                    {partner.description && (
                      <p className="text-sm md:text-base text-gray-600 mt-4 text-center leading-relaxed whitespace-pre-line">
                        {partner.description}
                      </p>
                    )}
                  </>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section id="sponsors" className="relative py-16 lg:py-24 px-4 bg-gradient-to-b from-[#114845]/5 via-[#114845]/3 to-background overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-24 lg:mb-32"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/10 rounded-full blur-lg"></div>
                <div className="relative w-12 h-12 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full flex items-center justify-center border border-primary/30 shadow-memorial">
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <h3 className="font-memorial text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary via-accent to-success bg-clip-text text-transparent drop-shadow-sm">
                {content?.sponsorsTitle}
              </span>
            </h3>
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center justify-center gap-2"
            >
              <div className="h-1 w-12 bg-gradient-to-r from-transparent to-primary rounded-full"></div>
              <div className="h-1 w-2 bg-primary rounded-full"></div>
              <div className="h-1 w-12 bg-gradient-to-l from-transparent to-primary rounded-full"></div>
            </motion.div>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 lg:gap-16">
            {renderSponsorCategory(content?.technicalPartnerTitle, technicalPartners, 5)}
            {renderSponsorCategory(content?.financialSponsorTitle, financialSponsors, 2)}
            
            {renderSponsorCategory(content?.strategicSponsorTitle, strategicSponsors, 0)}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SponsorsSection;

